// SNAPSHOT codec (§20.4). The body (everything after the per-client header) is encoded once per
// room per snapshot; encodeSnapshot() prepends each client's 15-byte header.
import { MAP_TILE_COUNT, MAP_WEDGE_COUNT, MAX_BEANS } from '../constants';
import { EV, type SimEvent } from '../events';
import { MapId } from '../ids';
import { roundClock } from '../maps/clock';
import { createMapState, deriveBeanStats } from '../sim/world';
import type { Entity, MapState, World } from '../types';
import { BinReader, BinWriter } from './binary';
import {
  BEAN_SCHEMA, BinMsg, CM, ENT_SCHEMA, MODE_SCHEMA, SNAPSHOT_HEADER_BYTES, TILT_SCALE,
  readField, writeField, type SnapBean, type SnapEnt, type SnapMode,
} from './schema';

/** Event types the server never sends: derived locally from the clock or from map-state diffs (§20.10). */
export const STRIPPED_EVENTS: readonly number[] = [EV.PHASE, EV.COUNTDOWN, EV.HAZARD_WARN, EV.HAZARD_FIRE, EV.TILE, EV.WEDGE];
/** Overflow drop order, lowest priority first (§20.10). */
export const EVENT_DROP_ORDER: readonly number[] = [EV.HONK, EV.EMOTE, EV.LAND, EV.JUMP, EV.LUNGE, EV.BUMPER, EV.DUMMY_HIT];
export const MAX_SNAPSHOT_EVENTS = 255;
export const MAX_SNAPSHOT_ENTS = 255;

export function isStrippedEvent(type: number): boolean {
  return STRIPPED_EVENTS.includes(type);
}

export interface SnapshotHeader { roomTick: number; roundId: number; roundTick: number; ackTick: number; inputSlack: number }

export interface Snapshot extends SnapshotHeader {
  activeMask: number;
  /** Indexed by slot; null for inactive slots. */
  beans: (SnapBean | null)[];
  ents: SnapEnt[];
  /** Map state with the replicated fields of this map filled in; the rest are defaults. */
  map: MapState;
  mode: SnapMode;
  /** `tick` is the round tick the event happened on. */
  events: SimEvent[];
  /** Encoded size in bytes (stats). */
  bytes: number;
}

/** A room's shared snapshot body. `bytes` is valid until the writer that produced it is reused. */
export interface SnapshotBody { roundId: number; roundTick: number; bytes: Uint8Array }

/**
 * Events that go on the wire: stripped types removed, then capped at 255 by dropping the
 * lowest-priority types first (newest first within a type). If unprioritized events alone still
 * exceed the cap, the oldest 255 are kept. Order is preserved.
 */
export function selectSnapshotEvents(events: readonly SimEvent[]): SimEvent[] {
  const kept = events.filter((e) => !isStrippedEvent(e.type));
  let excess = kept.length - MAX_SNAPSHOT_EVENTS;
  for (const type of EVENT_DROP_ORDER) {
    for (let i = kept.length - 1; i >= 0 && excess > 0; i--) {
      if (kept[i].type === type) {
        kept.splice(i, 1);
        excess--;
      }
    }
    if (excess <= 0) break;
  }
  return excess > 0 ? kept.slice(0, MAX_SNAPSHOT_EVENTS) : kept;
}

function writeMapBlock(w: BinWriter, mapId: MapId, m: MapState): void {
  if (mapId === MapId.TEACUP) {
    w.q16(m.tiltX, TILT_SCALE);
    w.q16(m.tiltY, TILT_SCALE);
    w.u8(m.wedgeFallen);
    w.u8(m.wedgeWarn);
    w.u8(m.cupOccupant);
    w.u8(m.sdCursor);
    for (let i = 0; i < MAP_WEDGE_COUNT; i++) w.u8(m.wedgeOrder[i]);
  } else if (mapId === MapId.CAKE) {
    w.u8(m.sdCursor);
    for (let i = 0; i < MAP_TILE_COUNT; i++) w.u8(m.tileState[i]);
    for (let i = 0; i < MAP_TILE_COUNT; i++) w.u16(m.tileTimer[i]);
  }
}

function readMapBlock(r: BinReader, mapId: MapId): MapState {
  const m = createMapState();
  if (mapId === MapId.TEACUP) {
    m.tiltX = r.q16(TILT_SCALE);
    m.tiltY = r.q16(TILT_SCALE);
    m.wedgeFallen = r.u8();
    m.wedgeWarn = r.u8();
    m.cupOccupant = r.u8();
    m.sdCursor = r.u8();
    for (let i = 0; i < MAP_WEDGE_COUNT; i++) m.wedgeOrder[i] = r.u8();
  } else if (mapId === MapId.CAKE) {
    m.sdCursor = r.u8();
    for (let i = 0; i < MAP_TILE_COUNT; i++) m.tileState[i] = r.u8();
    for (let i = 0; i < MAP_TILE_COUNT; i++) m.tileTimer[i] = r.u16();
  }
  return m;
}

function applyMapBlock(dst: MapState, src: MapState, mapId: MapId): void {
  if (mapId === MapId.TEACUP) {
    dst.tiltX = src.tiltX;
    dst.tiltY = src.tiltY;
    dst.wedgeFallen = src.wedgeFallen;
    dst.wedgeWarn = src.wedgeWarn;
    dst.cupOccupant = src.cupOccupant;
    dst.sdCursor = src.sdCursor;
    for (let i = 0; i < MAP_WEDGE_COUNT; i++) dst.wedgeOrder[i] = src.wedgeOrder[i];
  } else if (mapId === MapId.CAKE) {
    dst.sdCursor = src.sdCursor;
    for (let i = 0; i < MAP_TILE_COUNT; i++) {
      dst.tileState[i] = src.tileState[i];
      dst.tileTimer[i] = src.tileTimer[i];
    }
  }
}

/** Encodes the shared body for world `w` and the events accumulated since the last snapshot. */
export function encodeSnapshotBody(w: World, events: readonly SimEvent[], out: BinWriter = new BinWriter(1024)): SnapshotBody {
  out.reset();
  let activeMask = 0;
  for (let slot = 0; slot < MAX_BEANS; slot++) if (w.beans[slot].active) activeMask |= 1 << slot;
  out.u8(activeMask);
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const b = w.beans[slot];
    if (!b.active) continue;
    for (const f of BEAN_SCHEMA) writeField(out, f.type, f.get(b), f.scale);
  }

  const entCount = Math.min(w.ents.length, MAX_SNAPSHOT_ENTS);
  out.u8(entCount);
  for (let i = 0; i < entCount; i++) {
    const e = w.ents[i];
    for (const f of ENT_SCHEMA) writeField(out, f.type, f.get(e), f.scale);
  }

  writeMapBlock(out, w.cfg.mapId, w.map);
  for (const f of MODE_SCHEMA) writeField(out, f.type, f.get(w.mode), f.scale);

  const evs = selectSnapshotEvents(events);
  out.u8(evs.length);
  for (const ev of evs) {
    out.u8(ev.type);
    out.u8(w.tick - ev.tick);
    out.u8(ev.a);
    out.u8(ev.b);
    out.i16(ev.c);
    out.i16(ev.d);
    out.q16(ev.x, CM);
    out.q16(ev.y, CM);
  }
  return { roundId: w.roundId, roundTick: w.tick, bytes: out.toBytes() };
}

/**
 * Prepends one client's header to a shared body. `roundId` travels as u8 (it wraps at 256);
 * `inputSlack` is clamped to ±127.
 */
export function encodeSnapshot(roomTick: number, body: SnapshotBody, ackTick: number, inputSlack: number): Uint8Array {
  const w = new BinWriter(SNAPSHOT_HEADER_BYTES + body.bytes.byteLength);
  w.u8(BinMsg.SNAPSHOT);
  w.u32(roomTick);
  w.u8(body.roundId & 0xff);
  w.i32(body.roundTick);
  w.u32(ackTick);
  w.i8(Math.max(-127, Math.min(127, inputSlack)));
  w.bytes(body.bytes);
  return w.toBytes();
}

function readHeader(r: BinReader): SnapshotHeader {
  if (r.u8() !== BinMsg.SNAPSHOT) throw new RangeError('not a SNAPSHOT frame');
  return { roomTick: r.u32(), roundId: r.u8(), roundTick: r.i32(), ackTick: r.u32(), inputSlack: r.i8() };
}

/** Reads only the 15-byte header (to route a snapshot before decoding its map block). */
export function readSnapshotHeader(data: ArrayBuffer | ArrayBufferView): SnapshotHeader {
  return readHeader(new BinReader(data));
}

/**
 * Decodes a SNAPSHOT frame. The MAP block layout depends on the round's map, which the client
 * knows from the `round` message for `roundId`. Throws a RangeError on truncated input.
 */
export function decodeSnapshot(data: ArrayBuffer | ArrayBufferView, mapId: MapId): Snapshot {
  const r = new BinReader(data);
  const header = readHeader(r);
  const activeMask = r.u8();
  const beans: (SnapBean | null)[] = [];
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    if ((activeMask & (1 << slot)) === 0) {
      beans.push(null);
      continue;
    }
    const rec = { slot } as SnapBean;
    const fields = rec as unknown as Record<string, number>;
    for (const f of BEAN_SCHEMA) fields[f.name] = readField(r, f.type, f.scale);
    beans.push(rec);
  }

  const ents: SnapEnt[] = [];
  const entCount = r.u8();
  for (let i = 0; i < entCount; i++) {
    const rec = {} as SnapEnt;
    const fields = rec as unknown as Record<string, number>;
    for (const f of ENT_SCHEMA) fields[f.name] = readField(r, f.type, f.scale);
    ents.push(rec);
  }

  const map = readMapBlock(r, mapId);
  const mode = {} as SnapMode;
  const modeFields = mode as unknown as Record<string, number>;
  for (const f of MODE_SCHEMA) modeFields[f.name] = readField(r, f.type, f.scale);

  const events: SimEvent[] = [];
  const evCount = r.u8();
  for (let i = 0; i < evCount; i++) {
    const type = r.u8();
    const tick = header.roundTick - r.u8();
    events.push({ type, tick, a: r.u8(), b: r.u8(), c: r.i16(), d: r.i16(), x: r.q16(CM), y: r.q16(CM) });
  }

  return { ...header, activeMask, beans, ents, map, mode, events, bytes: r.length };
}

function blankEntity(): Entity {
  return { id: 0, kind: 1, owner: 0, pos: { x: 0, y: 0 }, vel: { x: 0, y: 0 }, z: 0, vz: 0, t: 0, timer: 0, data: 0, data2: 0 };
}

/**
 * Writes every replicated field of `snap` into `w` (the client's authWorld) and re-derives the
 * round phase and Big Shake stats. Server-only state (touch credit, RNG, lunge masks, fuse,
 * history) is left untouched. Events are not applied: they are delivered by the EventScheduler.
 * Returns false (and changes nothing) if the snapshot belongs to a different round.
 */
export function applySnapshotToWorld(w: World, snap: Snapshot): boolean {
  if (snap.roundId !== (w.roundId & 0xff)) return false;
  w.tick = snap.roundTick;
  w.phase = roundClock(w.cfg.mapId, w.cfg.modeId, w.tick, w.cfg.capTickOverride).phase;

  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const b = w.beans[slot];
    const rec = snap.beans[slot];
    b.active = rec !== null;
    if (!rec) continue;
    for (const f of BEAN_SCHEMA) f.set(b, rec[f.name]);
    deriveBeanStats(b);
  }

  const prev = w.ents;
  w.ents = snap.ents.map((rec) => {
    const e = prev.find((p) => p.id === rec.id) ?? blankEntity();
    for (const f of ENT_SCHEMA) f.set(e, rec[f.name]);
    return e;
  });

  applyMapBlock(w.map, snap.map, w.cfg.mapId);
  for (const f of MODE_SCHEMA) f.set(w.mode, snap.mode[f.name]);
  return true;
}
